import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { fg } from '@confluence/feature-gating';

import { serverToUiContentMode, queryStatusFor, type UiContentMode } from '../utils/contentMode.ts';

import type {
	useEditorContentModeValueQuery$data,
	useEditorContentModeValueQuery$variables,
} from './__generated__/useEditorContentModeValueQuery.graphql.ts';

const UseEditorContentModeValueQuery = graphql`
	query useEditorContentModeValueQuery($contentId: ID!, $isDraft: Boolean!, $status: [String]) {
		content(id: $contentId, status: $status) {
			nodes {
				id
				contentProperties {
					latest @skip(if: $isDraft) {
						contentMode
					}
					draft @include(if: $isDraft) {
						contentMode
					}
				}
			}
		}
	}
`;

/**
 * Hook to get the editor content mode value for a particular page/blog from the backend.
 */
export function useEditorContentModeValue({
	contentId,
	isArchived,
}: {
	contentId?: string;
	isArchived?: boolean;
}): {
	mode?: UiContentMode;
	isLoading: boolean;
	error: unknown | null;
} {
	const isLivePage = useIsCurrentPageLive();
	const isEditorPage = useIsEditorPage();

	const isDraft =
		!isLivePage && Boolean(isEditorPage) && fg('platform_editor_content_mode_button_mvp');
	const status = queryStatusFor(isDraft, isArchived);

	const { data, error, loading } = useQueryInterop<
		useEditorContentModeValueQuery$data,
		useEditorContentModeValueQuery$variables
	>(UseEditorContentModeValueQuery, {
		variables: { contentId: contentId!, isDraft, status: [status] },
		fetchPolicy: 'cache-and-network',
		skip: !fg('platform_editor_content_mode_button_mvp') || !contentId, // avoid starting network flow
	});

	let serverModeRaw: string | null | undefined;

	if (fg('platform_editor_content_mode_button_mvp')) {
		const contentProps = data?.content?.nodes?.[0]?.contentProperties;

		if (isDraft) {
			serverModeRaw = contentProps?.draft?.contentMode;
		} else {
			serverModeRaw = contentProps?.latest?.contentMode;
		}
	} else {
		serverModeRaw = undefined;
	}

	const mode: UiContentMode = serverToUiContentMode(serverModeRaw);

	return useMemo(
		() => ({
			mode,
			isLoading: fg('platform_editor_content_mode_button_mvp') ? Boolean(loading) : false,
			error: fg('platform_editor_content_mode_button_mvp') ? (error ?? null) : null,
		}),
		[mode, loading, error],
	);
}
